/**
 * A PLACE, GUARDED. Every place a signed-in person reaches (Home, Schedule, Payments, Messages,
 * Feedback) is read from the Worker when it is shown, through the one scoped read there, and what
 * that read answers decides what is drawn, never this file:
 *
 * - while the question is out, nothing but the page colour, so no screen flashes past;
 * - 401, the session has ended: the door, which is the way back in. When it comes of a read again,
 *   the door takes the place of the place she was on, with no move, and takes the focus to its
 *   heading (`screens/Door.tsx`);
 * - 404, no household to show: the page that says to call. The Worker reads the membership afresh
 *   on every request, so this is also where a person lands when ops revokes her mid-session. When
 *   it comes of a read again, the page takes the place of the place she was on, with no move, and
 *   takes the focus to its heading (`screens/NoMembership.tsx`). A place only some may see draws
 *   `notFound` instead: Payments answers a member 404 too, and sends her to Home, whose own read
 *   says which she is;
 * - 404 with the Worker's word that it may still be on its way, which only an invoice's own page
 *   reads, while her first fill is still arriving (ops's plan, section 7, ask 5): what the screen
 *   draws for it (`arriving`), handed the way to read it again, and whether the Worker says too that
 *   a code opens it, a client's door ops pushed for her own address waiting for her code (the review
 *   of 7e33d61, finding 1), and kept, as a place drawn is, when a read again does not go through;
 * - a place it cannot read, or a request that failed: the shell, with the header's Call button and
 *   the nav, the place's own heading, and under it one line that says it did not go through, with
 *   the phone, so a move to it lands the focus on its heading as on any page
 *   (`RouteFocus.tsx`); or `failed`, for a place with something to say that needs no read, such as
 *   the page Stripe sends her back to;
 * - the place: whatever the screen draws from it, with its household handed down to the shell it
 *   is drawn in (`ShownHousehold`), which marks the nav from it (provisional P49).
 *
 * The place is asked for when the screen is shown, and again when the screen says so, after it has
 * asked the office for something: the `reload` handed to the screen with the place. The answer to
 * that second question replaces the place, a 401 or a 404 goes where the first would, and one that
 * did not go through leaves the place as it was, so a page that has just said a request was sent
 * does not turn into a failure because the read after it failed. `Entry` hands each screen the
 * signed-in address or number and the way to ask /api/me again, as `Signed`.
 *
 * EVERY OPEN TAB DRAWS A PAYMENT ON ITS WAY AT ONCE (slice V2, `docs/seam.md` P35). The replay of
 * the owner's walk of 2026-10-16 found a tab already open when she paid still offering Zelle and
 * Pay. So the place keeps a view of the payments on their way her browser keeps (`lib/paying.ts`),
 * which it hands down with its household (`ShownHousehold`), and which every Pay held back, the
 * nav's count and Home's line are drawn from (`useOnItsWay`). It listens for a change to them
 * (`onMarksChanged`): one made in this tab, as "I sent it by Zelle", is taken into the view at once;
 * one made in another tab is too where it holds an invoice anew, so its Pay, the nav's count and
 * Home's line stand down at once; and either way the place is read again, so what ops has pushed
 * since is drawn too. A mark another tab forgot, which it does on reading ops's push of how the
 * invoice stands, waits for this place's own read: each read that comes back takes the view afresh,
 * so this place never offers Pay again from its older read on the word of that tab's newer one.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { HouseholdShown, PlaceAnswer, SignedInWith } from '../api.ts';
import { marksKept, onItsWayIn, onMarksChanged } from '../lib/paying.ts';
import { Door } from '../screens/Door.tsx';
import { NoMembership } from '../screens/NoMembership.tsx';
import { FAILED_BEFORE_PHONE } from '../words.ts';
import type { Tab } from './BottomNav.tsx';
import { Phone } from './Phone.tsx';
import { Shell, ShownHousehold } from './Shell.tsx';

/** What `Entry` hands a place once /api/me has said a home is linked. */
export interface Signed {
  /**
   * The address or mobile number that signed in, for the page that says to call and for a fresh
   * code, which goes to it (home #11).
   */
  readonly signedInWith: SignedInWith;
  /** Asks /api/me again, after a sign-out. */
  readonly onSignedOut: () => void;
  /**
   * How many invitations wait for a code to that address or number, as /api/me says it, which Home
   * says above its content (`InvitationWaits`, `docs/seam.md` P118). None where it is not given.
   */
  readonly invitationsWaiting?: number;
  /**
   * Asks /api/me again with the screen still drawn, once a code opened what waited, so the count
   * reads afresh, and a home it linked is drawn where the page that says to call was.
   */
  readonly onInvitationsOpened?: () => void;
}

interface GuardedProps<T> {
  /** The one read this place is drawn from. */
  readonly load: () => Promise<PlaceAnswer<T>>;
  readonly signed: Signed;
  /** The tab the place is, for the shell a failed read is drawn in; null for none of the four. */
  readonly at: Tab | null;
  /** The place's own heading word, from `words.ts`, the main heading of a failed read. */
  readonly heading: string;
  /** Draws the place, given it and the way to read it again. */
  readonly children: (place: T, reload: () => void) => ReactNode;
  /** What a 404 draws in place of the page that says to call, for a place only some may see. */
  readonly notFound?: ReactNode;
  /**
   * What a 404 that says the place may still be on its way draws, given the way to read it again,
   * for an invoice's own page while her first fill is still arriving (ops's plan, section 7, ask 5),
   * and whether the Worker says a code opens it (`invited`, the review of 7e33d61, finding 1).
   * Without it, such a 404 is any other.
   */
  readonly arriving?: (reload: () => void, invited: boolean) => ReactNode;
  /**
   * What a place it cannot read draws in place of the line that says it did not go through, for a
   * place with something to say that needs no read: the page Stripe sends her back to.
   */
  readonly failed?: ReactNode;
}

/** Whether a read drew something to keep: the place, or the place as on its way. */
const drawnAlready = (held: PlaceAnswer<unknown> | null): boolean =>
  held !== null &&
  (held.outcome === 'shown' || (held.outcome === 'no_home' && held.arriving === true));

/** The household a place read gives, or none for a place that gives none, as Feedback's none. */
function householdOf(place: unknown): HouseholdShown | undefined {
  if (typeof place !== 'object' || place === null || !('household' in place)) return undefined;
  return (place as { readonly household: HouseholdShown }).household;
}

export function Guarded<T>({
  load,
  signed,
  at,
  heading,
  children,
  notFound,
  arriving,
  failed,
}: GuardedProps<T>) {
  const [answer, setAnswer] = useState<PlaceAnswer<T> | null>(null);
  const [asked, setAsked] = useState(0);
  /**
   * The place's view of the payments on their way her browser keeps (`lib/paying.ts`, P35), which
   * every Pay held back, the nav's count and Home's line are drawn from.
   */
  const [kept, setKept] = useState(marksKept);

  useEffect(() => {
    let live = true;
    void load().then((got) => {
      if (!live) return;
      // A place already drawn, or drawn as on its way, stays drawn when the read after it did not
      // go through.
      setAnswer((held) => (drawnAlready(held) && got.outcome === 'failed' ? held : got));
      // What ops has pushed is drawn with the payments on their way as her browser keeps them now,
      // and the view stays as it was with a place that stays as it was.
      if (got.outcome !== 'failed') setKept(marksKept());
    });
    return () => {
      live = false;
    };
    // Asked when the place is shown and at each reload: a new `load` on a later render is not a
    // new question.
  }, [asked]);

  const reload = useCallback(() => setAsked((count) => count + 1), []);

  // A payment on its way changed: take the view afresh now where this tab changed it, or another
  // tab held an invoice anew, so a Pay is held back at once; and where another tab changed it, read
  // the place again too. That tab forgets a mark on reading ops's push of how the invoice stands,
  // which this place has not read, so a mark forgotten elsewhere leaves the view to this place's
  // own read.
  useEffect(
    () =>
      onMarksChanged(({ elsewhere, marked }) => {
        if (!elsewhere || marked) setKept(marksKept());
        if (elsewhere) reload();
      }),
    [reload],
  );
  const onItsWay = useMemo(() => (invoiceId: string) => onItsWayIn(kept, invoiceId), [kept]);

  if (answer === null) return <div className="min-h-dvh bg-surface-page" />;
  switch (answer.outcome) {
    case 'signed_out':
      // Read again only from a place drawn, so a door after a reload replaces where she was.
      return <Door inPlace={asked > 0} />;
    case 'no_home':
      if (answer.arriving === true && arriving !== undefined) {
        return arriving(reload, answer.invited === true);
      }
      // Read again only from a place drawn, so the page after a reload replaces where she was.
      return (
        notFound ?? (
          <NoMembership
            signedInWith={signed.signedInWith}
            onSignedOut={signed.onSignedOut}
            inPlace={asked > 0}
            invitationsWaiting={signed.invitationsWaiting}
            onInvitationsOpened={() => {
              // The count afresh, and the place again, which may now find what the code opened.
              signed.onInvitationsOpened?.();
              reload();
            }}
          />
        )
      );
    case 'failed':
      return (
        failed ?? (
          <Shell at={at}>
            <div>
              <h1 className="font-serif text-h2 font-semibold text-pretty text-text-body">
                {heading}
              </h1>
              <p className="mt-2 text-body text-pretty">
                {FAILED_BEFORE_PHONE} <Phone after="." />
              </p>
            </div>
          </Shell>
        )
      );
    case 'shown':
      return (
        <ShownHousehold household={householdOf(answer.place)} onItsWay={onItsWay}>
          {children(answer.place, reload)}
        </ShownHousehold>
      );
  }
}
